import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { CampaignQuoteTemplateModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/campaignQuoteTemplate/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/campaignQuoteTemplate/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/campaignQuoteTemplate/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/campaignQuoteTemplate/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/campaignQuoteTemplate/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/campaignQuoteTemplate/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/campaignQuoteTemplate/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/campaignQuoteTemplate/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/campaignQuoteTemplate/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/campaignQuoteTemplate/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/campaignQuoteTemplate/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/campaignQuoteTemplate/DELETE_FAILURE'

export type CampaignQuoteTemplateModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: CampaignQuoteTemplateModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCampaignQuoteTemplates(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaignQuoteTemplates.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/campaign_quote_templates.json', options)
      .then(({ data }: { data: CampaignQuoteTemplateModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCampaignQuoteTemplate(campaignQuoteTemplateId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.campaignQuoteTemplates.loadedForKeys]
    const loadedIds = [...state.campaignQuoteTemplates.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (campaignQuoteTemplateId && !loadedIds.includes(campaignQuoteTemplateId)){
      loadedIds.push(campaignQuoteTemplateId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/campaign_quote_templates/${campaignQuoteTemplateId}.json`, options)
      .then(({ data }: { data: CampaignQuoteTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignQuoteTemplate(
  campaignQuoteTemplate: Partial<CampaignQuoteTemplateModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_quote_template: campaignQuoteTemplate,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_quote_templates.json', options, config)
      .then(({ data }: { data: CampaignQuoteTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignQuoteTemplateFromQuote(
  createCampaignQuoteTemplateParams: {
    campaignQuoteId: number,
    campaignQuoteTemplate: Partial<CampaignQuoteTemplateModel>,
  },
  options: EntityOptions = {},
) {
  const { campaignQuoteId, campaignQuoteTemplate } = createCampaignQuoteTemplateParams
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_quote_id: campaignQuoteId,
      campaign_quote_template: campaignQuoteTemplate,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_quote_templates/create_from_quote.json', options, config)
      .then(({ data }: { data: CampaignQuoteTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCampaignQuoteTemplate(
  campaignQuoteTemplate: UpdateParams<CampaignQuoteTemplateModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign_quote_template: campaignQuoteTemplate,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quote_templates/${campaignQuoteTemplate.id}.json`, options, config)
      .then(({ data }: { data: CampaignQuoteTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCampaignQuoteTemplateSectionItemOrder(
  campaignQuoteTemplate: CampaignQuoteTemplateModel,
  sortedSectionItemIds: {
    [key: string]: number[],
  },
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      sorted_section_items: sortedSectionItemIds,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quote_templates/${campaignQuoteTemplate.id}/update_section_item_sort.json`, options, config)
      .then(({ data }: { data: CampaignQuoteTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function duplicateCampaignQuoteTemplate(
  campaignQuoteTemplate: UpdateParams<CampaignQuoteTemplateModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/campaign_quote_templates/${campaignQuoteTemplate.id}/duplicate.json`, options, config)
      .then(({ data }: { data: CampaignQuoteTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCampaignQuoteTemplateTotal(
  campaignQuoteTemplateId: number,
  total: number,
) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const { campaignQuoteTemplates } = state.entities

    const campaignQuoteTemplate = campaignQuoteTemplates[campaignQuoteTemplateId]

    if (campaignQuoteTemplate){
      const updatedQuoteTemplate = { ...campaignQuoteTemplate, total }

      const normalizedJson = normalize(updatedQuoteTemplate, Schemas.CAMPAIGN_QUOTE_TEMPLATE)
      // @ts-expect-error TS2345
      dispatch(updateEntities(normalizedJson))
      dispatch(updateSuccess())
    } else {
      dispatch(updateFailure(['Campaign Quote Template does not exist.']))
    }
  }
}

export function recalculateCampaignQuoteTemplatePrices(
  campaignQuoteTemplate: UpdateParams<CampaignQuoteTemplateModel>,
  options: EntityOptions = {},
) {
  const { id } = campaignQuoteTemplate

  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quote_templates/${id}/recalculate_prices.json`, options, config)
      .then(({ data }: { data: CampaignQuoteTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function toggleHideCampaignQuoteTemplate(
  campaignQuoteTemplate: CampaignQuoteTemplateModel,
  options: EntityOptions = {},
) {
  const { id } = campaignQuoteTemplate

  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quote_templates/${id}/toggle_hidden.json`, options, config)
      .then(({ data }: { data: CampaignQuoteTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function deleteCampaignQuoteTemplate(campaignQuoteTemplate: DeleteParams<CampaignQuoteTemplateModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/campaign_quote_templates/${campaignQuoteTemplate.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: CampaignQuoteTemplateModuleState = initialState,
  action: Action = { type: '' },
): CampaignQuoteTemplateModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
